import './update-trade.scss'

import { Button, notification, Popconfirm } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import { DatePicker, Form, FormItem, Input, InputNumber, SubmitButton } from 'formik-antd'
import React, { useEffect, useState } from 'react'
import { Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchDetailByDomain } from '../../../api'
import { deleteTrade as deleteTradeApi } from '../../../api/delete-trade'
import { emptyCharacter } from '../../../components/EmptyChar'
import { parseApiErrorResponse } from '../../../components/forms/utils'
import { config } from '../../../config'
import { momentDateHandler } from '../../../date/moment-date-handler'
import { formatCurrency, toFixed } from '../../../formatters'
import { httpClient } from '../../../util/http-client'
import { messages } from '../messages'
import { ManagedFund, updateManagedFundFormInitialValues, UpdateManagedFundFormValues } from '../models'
import { managedFundValidationSchema } from './validators'

const { formatIso, newDate, formatDate, isBefore } = momentDateHandler

interface UpdateManagedFundsProps {
  tradeId: number
  domain: string
  onSuccess: () => void
}

export const UpdateManagedFund = ({ tradeId, domain, onSuccess }: UpdateManagedFundsProps) => {
  const [tradeFormValues, setTradeFormValues] = useState<UpdateManagedFundFormValues>(
    updateManagedFundFormInitialValues,
  )

  const { data: trade, error } = useSWR<ManagedFund>(
    tradeId ? [`/trades/${tradeId}`, domain] : null,
    fetchDetailByDomain,
    { suspense: true },
  )

  useEffect(() => {
    if (!trade) return
    trade.principal = +toFixed({ value: trade.principal, precision: 2 })
    setTradeFormValues(convertEntityValuesToFormValues(trade))
  }, [trade])

  if (error) return <Row>{`Could not load trade ID ${tradeId}.`}</Row>

  const onSubmit = async (
    tradeUpdateFormValues: UpdateManagedFundFormValues,
    { setErrors, setSubmitting }: FormikHelpers<UpdateManagedFundFormValues>,
  ) => {
    try {
      await httpClient(`/trades/managed-funds/${tradeId}`, {
        method: 'PUT',
        domain,
        data: convertManagedFundToUpdateManagedFundPayload(tradeUpdateFormValues),
      })

      notification.success({ message: messages.common.success, description: messages.trade.update.success })

      onSuccess()
    } catch (error) {
      setErrors(parseApiErrorResponse(error))

      notification.error({ message: messages.common.error, description: messages.trade.update.error })
    } finally {
      setSubmitting(false)
    }
  }

  const deleteTrade = async () => {
    try {
      await deleteTradeApi({ tradeId, domain })
      notification.success({ message: messages.common.success, description: messages.trade.delete.success })
      onSuccess()
    } catch (error) {
      notification.error({ message: messages.common.error, description: error.error_message })
    }
  }

  return (
    <Formik
      initialValues={tradeFormValues}
      validationSchema={managedFundValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ submitForm }) => (
        <Form name="update-managed-funds">
          <Row justifyContentSpaceBetween className="trade-details">
            <dl>
              <dt>ID</dt>
              <dd>{trade?.id}</dd>
            </dl>
            <dl>
              <dt>Instrument</dt>
              <dd>{trade?.instrumentCode}</dd>
            </dl>
            <dl>
              <dt>Issuer</dt>
              <dd>
                {trade?.issuer?.name} ({trade?.issuer?.code})
              </dd>
            </dl>
            <dl>
              <dt>APIR</dt>
              <dd>{trade?.instrumentDetails?.apir || emptyCharacter}</dd>
            </dl>
            <dl>
              <dt>Operation</dt>
              <dd>{trade?.instrumentDetails?.operation || emptyCharacter}</dd>
            </dl>
            <dl>
              <dt>Transaction Date</dt>
              <dd>{formatDate({ date: newDate(trade?.transactionDate) }) || emptyCharacter}</dd>
            </dl>
            <dl>
              <dt>Principal ($)</dt>
              <dd>{formatCurrency({ value: trade?.principal })}</dd>
            </dl>
            <dl>
              <dt>Quantity</dt>
              <dd>{trade?.instrumentDetails?.quantity}</dd>
            </dl>
            <dl>
              <dt>Price</dt>
              <dd>{formatCurrency({ value: trade?.instrumentDetails?.price })}</dd>
            </dl>
          </Row>

          <FormItem label="Update Transaction Date" name="transactionDate" required>
            <DatePicker
              name="transactionDate"
              aria-label="transactionDate"
              format={config.defaultDateFormat}
              disabledDate={(date) => isBefore({ firstDate: date, secondDate: newDate(trade?.transactionDate) })}
            />
          </FormItem>

          <FormItem label="Quantity to Keep" name="quantityToKeep" required>
            <InputNumber name="quantityToKeep" aria-label="quantityToKeep" min={0} />
          </FormItem>

          <FormItem label="Unit Price $" name="price" required>
            <InputNumber name="price" aria-label="price" min={0} />
          </FormItem>

          <FormItem label="Reference" name="reference">
            <Input name="reference" aria-label="reference" />
          </FormItem>

          <FormItem label="Bank Reference" name="bankReference">
            <Input name="bankReference" aria-label="bankReference" />
          </FormItem>

          <FormItem label="Note" name="note">
            <Input name="note" aria-label="note" />
          </FormItem>

          <FormItem name="actions">
            <Row className="action-buttons">
              <Popconfirm title="Are you sure you want to delete this trade?" onConfirm={deleteTrade}>
                <Button danger>Delete</Button>
              </Popconfirm>
              <Popconfirm title="Are you sure you want to update this trade?" onConfirm={submitForm}>
                <SubmitButton className="update-trade" htmlType="submit" type="primary">
                  Update Managed Fund
                </SubmitButton>
              </Popconfirm>
            </Row>
          </FormItem>
        </Form>
      )}
    </Formik>
  )
}

const convertEntityValuesToFormValues = (managedFund: ManagedFund): UpdateManagedFundFormValues => ({
  quantityToKeep: managedFund?.instrumentDetails?.quantity,
  price: managedFund?.instrumentDetails?.price,
  transactionDate: managedFund.transactionDate,
  note: managedFund.note,
  reference: managedFund.reference,
  bankReference: managedFund.bankReference,
})

export const convertManagedFundToUpdateManagedFundPayload = (values: UpdateManagedFundFormValues) => {
  const updateTradePayload = { ...values }

  if (updateTradePayload.transactionDate) {
    updateTradePayload.transactionDate = formatIso({ date: newDate(updateTradePayload.transactionDate as Date) })
  }

  return updateTradePayload
}
